import { useEffect, useRef } from 'react';
import { db } from '../firebase';
import { flushPendingRuns } from '../utils/pendingRunsQueue';
import { flushPendingRunMetadataUpdates } from '../utils/pendingRunMetadataQueue';
import { patchCachesAfterPendingRunSynced } from '../utils/runListCache';

/**
 * Global background sync for offline-created runs.
 * Keeps queue flush independent from whichever page is currently open.
 */
export default function usePendingRunsAutoSync(user) {
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!user) return undefined;

    const runSyncOnce = async () => {
      if (!navigator.onLine || inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        await flushPendingRuns(db, user, {
          onSynced: ({ entry, firestoreId, runForUi }) => {
            if (!entry?.localDraftId) return;
            try {
              patchCachesAfterPendingRunSynced(user.uid, entry.localDraftId, firestoreId, runForUi);
            } catch (err) {
              console.warn('Could not patch caches after queue sync:', err);
            }
          },
        });
        await flushPendingRunMetadataUpdates(db);
      } catch (err) {
        console.warn('Background pending run sync failed:', err);
      } finally {
        inFlightRef.current = false;
      }
    };

    runSyncOnce();
    const onOnline = () => runSyncOnce();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') runSyncOnce();
    };
    const intervalId = window.setInterval(runSyncOnce, 20000);
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user]);
}
